import { useCallback, useEffect, useRef, useState } from 'react'
import { getMPDataWithParams } from 'src/mentor/services'
import {
  isCommonResponseSuccessfulV2,
  getDataMapFromCommonResponse,
} from 'src/mentor/helpers/utilityFunctions'

const INSIGHTS_API = '/api/secure/practitioner/report-insights'

/**
 * Fetches practitioner "My Reports" insights for a LIST of periods (one call each, in parallel) —
 * the backing data for period comparison and the same-month growth trajectory. Each period is
 * `{ from, to, ... }`; the result is `[{ period, data }]` in the SAME order as `periods` (a failed
 * or empty period yields `data: null` rather than dropping the row, so callers can render a gap).
 *
 * Fires only when `enabled` and refetches only when the query key (reportType | discipline |
 * clinicLocationId | the from:to of every period) changes — so toggling compare on/off or flipping
 * the metric doesn't refetch.
 */
export default function usePractitionerReportInsightsSeries({
  enabled,
  reportType,
  discipline,
  clinicBrandId,
  clinicLocationId,
  periods,
}) {
  const [series, setSeries] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const fetchedKeyRef = useRef(null)

  const periodsKey = (periods || []).map((p) => `${p.from}:${p.to}`).join('|')
  const queryKey = [reportType, discipline || '', clinicLocationId || '', periodsKey].join('~')

  const load = useCallback(async () => {
    if (!reportType || !clinicLocationId || !periods || periods.length === 0) return
    setLoading(true)
    setError(null)
    try {
      const results = await Promise.all(
        periods.map(async (p) => {
          try {
            const resp = await getMPDataWithParams(INSIGHTS_API, {
              reportType,
              clinicBrandId,
              clinicLocationId,
              from: p.from,
              to: p.to,
              ...(discipline ? { discipline } : {}),
            })
            if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
              return { period: p, data: getDataMapFromCommonResponse(resp) || null }
            }
            return { period: p, data: null }
          } catch (e) {
            return { period: p, data: null }
          }
        }),
      )
      setSeries(results)
    } catch (e) {
      setError('Could not load comparison')
      fetchedKeyRef.current = null // allow a retry
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, discipline, clinicBrandId, clinicLocationId, periodsKey])

  useEffect(() => {
    if (!enabled) return
    if (fetchedKeyRef.current === queryKey) return
    fetchedKeyRef.current = queryKey
    load()
  }, [enabled, queryKey, load])

  return { series, loading, error }
}
